import React from "react";
import { TextField, Switch, FormControlLabel } from "@mui/material";
import {
    Control,
    PageField,
    Panel,
    TablePage,
    TableRelationship,
    isControl,
    isField
} from "../../types/interfaces";
import { isPanel } from "../../utils/layoutUtils";



interface Props {
    control: PageField | Control | Panel;
    onChange: (updated: PageField | Control | Panel) => void;
    relationships?: TableRelationship[];  //  optional, required for RELATED_TABLE
    availablePages?: TablePage[]; // optional for page dropdowns
}


const ControlProperties: React.FC<Props> = ({
    control,
    onChange,
    relationships = [],
    availablePages = []
}) => {

    const handleChange = (key: string, value: any) => {
        onChange({ ...control, [key]: value });
    };

    return (
        <div className="control-properties">
            {isPanel(control) && (
                <>
                    <TextField
                        label="Name"
                        value={control.name || ""}
                        fullWidth
                        margin="dense"
                        onChange={(e) => handleChange("name", e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                    />

                    <TextField
                        label="CSS Style"
                        value={control.cssClass || ""}
                        fullWidth
                        margin="dense"
                        onChange={(e) => handleChange("cssClass", e.target.value.replace(/[^a-zA-Z0-9-_ ]/g, ""))}
                    />

                    <TextField
                        select
                        label="Alignment"
                        value={(control as any).alignment || "left"}
                        fullWidth
                        margin="dense"
                        SelectProps={{ native: true }}
                        onChange={(e) => handleChange("alignment", e.target.value)}
                    >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                    </TextField>

                    <TextField
                        label="Number of Columns"
                        type="number"
                        value={(control as any).columns}
                        fullWidth
                        margin="dense"
                        inputProps={{ min: 1, max: 4 }}
                        onChange={(e) => handleChange("columns", parseInt(e.target.value))}
                    />

                    <TextField
                        label="Number of Rows"
                        type="number"
                        value={(control as any).rows || 1}
                        fullWidth
                        margin="dense"
                        inputProps={{ min: 1 }}
                        onChange={(e) => handleChange("rows", parseInt(e.target.value))}
                    />
                </>
            )}
            {isField(control) && (
                <>
                    <TextField
                        label="Field"
                        value={control.fieldName}
                        fullWidth
                        margin="dense"
                        InputProps={{ readOnly: true }}
                    />

                    <TextField
                        label="Display Mask"
                        value={control.displayMask || ""}
                        fullWidth
                        margin="dense"
                        onChange={(e) => handleChange("displayMask", e.target.value)}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!control.required}
                                onChange={(e) => handleChange("required", e.target.checked)}
                            />
                        }
                        label="Required"
                    />

                    {
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!control.readOnly}
                                onChange={(e) => handleChange("readOnly", e.target.checked)}
                            />
                        }
                        label="Read Only"
                    />
                    }
                </>
            )}

            {isControl(control) && control.type === "RELATED_TABLE" && (
                <>
                    <TextField
                        select
                        fullWidth
                        margin="dense"
                        label="Related Table"
                        SelectProps={{ native: true }}
                        value={(control as any).relationshipId || ""}
                        onChange={(e) => handleChange("relationshipId", e.target.value)}
                    >
                        <option value="">Select relationship...</option>
                        {relationships?.map(rel => (
                            <option key={rel.relationshipID} value={rel.relationshipID}>
                                {rel.childTableID}
                            </option>
                        ))}
                    </TextField>

                    <TextField
                        label="Fixed Filters"
                        fullWidth
                        margin="dense"
                        multiline
                        placeholder={`e.g. Status = 'Active' AND DueDate < GETDATE()`}
                        value={(control as any).fixedFilter || ""}
                        onChange={(e) => handleChange("fixedFilter", e.target.value)}
                    />

                    {/* Field Mapping Section */}
                    {/* You could eventually make this dynamic with + Add Row */}
                    <TextField
                        label="Field Mapping (JSON format)"
                        fullWidth
                        margin="dense"
                        multiline
                        placeholder='[{"sourceField":"PageField1","targetField":"RelatedField1"}]'
                        value={JSON.stringify((control as any).fieldLinks || [], null, 2)}
                        onChange={(e) => {
                            try {
                                const parsed = JSON.parse(e.target.value);
                                handleChange("fieldLinks", parsed);
                            } catch {
                                // silently fail on invalid JSON for now
                            }
                        }}
                    />

                    <TextField
                        label="Display Columns (comma-separated)"
                        fullWidth
                        margin="dense"
                        placeholder="firstName,lastName,status"
                        value={(control as any).displayFields?.join(",") || ""}
                        onChange={(e) => handleChange("displayFields", e.target.value.split(",").map(x => x.trim()))}
                    />

                    {/* Page Selectors */}
                    {["viewPageId", "editPageId", "addPageId"].map((key) => (
                        <TextField
                            key={key}
                            select
                            fullWidth
                            margin="dense"
                            label={key.replace("PageId", " Page")}
                            SelectProps={{ native: true }}
                            value={(control as any)[key] || ""}
                            onChange={(e) => handleChange(key, e.target.value)}
                        >
                            <option value="">Select page...</option>
                            {availablePages
                                ?.filter(p => p.pageType.toUpperCase() === key.replace("PageId", "").toUpperCase())
                                .map(p => (
                                    <option key={p.pageID} value={p.pageID}>{p.pageName}</option>
                                ))}
                        </TextField>
                    ))}

                    {/* Permissions */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowView ?? true}
                                onChange={(e) => handleChange("allowView", e.target.checked)}
                            />
                        }
                        label="Allow View"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowAdd ?? true}
                                onChange={(e) => handleChange("allowAdd", e.target.checked)}
                            />
                        }
                        label="Allow Add"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowInlineAdd ?? false}
                                onChange={(e) => handleChange("allowInlineAdd", e.target.checked)}
                            />
                        }
                        label="Allow Inline Add"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowEdit ?? true}
                                onChange={(e) => handleChange("allowEdit", e.target.checked)}
                            />
                        }
                        label="Allow Edit"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowInlineEdit ?? false}
                                onChange={(e) => handleChange("allowInlineEdit", e.target.checked)}
                            />
                        }
                        label="Allow Inline Edit"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(control as any).allowDelete ?? true}
                                onChange={(e) => handleChange("allowDelete", e.target.checked)}
                            />
                        }
                        label="Allow Delete"
                    />
                </>
            )}

            {isControl(control) && control.type != "RELATED_TABLE" && (
                <>
                    {/* Common for all controls */}
                    <TextField
                        label="Name"
                        value={control.name || ""}
                        fullWidth
                        margin="dense"
                        onChange={(e) => handleChange("name", e.target.value)}
                    />
                    <TextField
                        label="Label"
                        value={control.label || ""}
                        fullWidth
                        margin="dense"
                        onChange={(e) => handleChange("label", e.target.value)}
                    />
                </>
            )}


        </div>
    );
};

export default ControlProperties;
